import API from '../../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { Card, Row, Col } from 'react-bootstrap';

import { Link } from 'react-router-dom';
/* Compact "how long ago" — absolute dates are kept in the title attribute so
   the exact timestamp is always reachable. */
const timeAgo = (value) => {
  if (!value) return '';

  const then = new Date(value).getTime();

  if (Number.isNaN(then)) return '';

  const seconds = Math.floor((Date.now() - then) / 1000);

  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;

  return new Date(value).toLocaleDateString();
};

const fullDate = (value) =>
  value ? new Date(value).toLocaleString() : 'Unknown date';

const initial = (name) =>
  String(name || '?')
    .trim()
    .charAt(0)
    .toUpperCase() || '?';

/* Order status → pill colour. Cancelled is included; the old badge fell
   through to the "pending" amber, which read as still-in-progress. */
const ORDER_TONES = {
  Delivered: { color: '#15803d', soft: '#f0fdf4', border: '#bbf7d0' },
  Shipped: { color: '#1d4ed8', soft: '#eff6ff', border: '#bfdbfe' },
  'Out For Delivery': { color: '#0e7490', soft: '#ecfeff', border: '#a5f3fc' },
  Cancelled: { color: '#b91c1c', soft: '#fef2f2', border: '#fecaca' },
  Processing: { color: '#b45309', soft: '#fffbeb', border: '#fde68a' }
};

const TICKET_TONES = {
  Open: { color: '#b91c1c', soft: '#fef2f2', border: '#fecaca' },
  'In Progress': { color: '#b45309', soft: '#fffbeb', border: '#fde68a' },
  Closed: { color: '#15803d', soft: '#f0fdf4', border: '#bbf7d0' }
};

const toneFor = (map, status) => map[status] || map.Processing || map.Open;

/* Colour the timeline dot by what kind of event it is. The feed only emits
   order and registration events today; anything else falls back to neutral. */
const activityTone = (message = '') => {
  if (/order/i.test(message)) return { color: '#1d4ed8', soft: '#eff6ff' };

  if (/register/i.test(message)) return { color: '#7c3aed', soft: '#f5f3ff' };

  return { color: '#78716c', soft: '#f5f5f4' };
};

/* Shared panel: header, optional footer link, loading + empty states */
function Panel({
  icon,
  title,
  hint,
  to,
  toLabel,
  loading,
  isEmpty,
  emptyIcon,
  emptyText,
  skeleton,
  children
}) {
  return (
    <Card className="border-0 h-100 ra-card">
      <Card.Body className="p-3 p-md-4 d-flex flex-column">
        <div className="d-flex align-items-center gap-3 mb-1">
          <span className="ra-icon">{icon}</span>

          <div className="flex-grow-1 min-w-0">
            <h5 className="ra-title">{title}</h5>
            {hint && <small className="ra-hint">{hint}</small>}
          </div>
        </div>

        <div className="ra-rule" />

        {loading ? (
          <div className="d-flex flex-column gap-2">
            {Array.from({ length: skeleton || 4 }).map((_, i) => (
              <div className="ra-skel-row" key={i}>
                <span className="ra-skel ra-skel-dot" />
                <div className="flex-grow-1">
                  <span className="ra-skel ra-skel-line" />
                  <span className="ra-skel ra-skel-line short" />
                </div>
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          <div className="ra-empty">
            <div className="ra-empty-icon">{emptyIcon}</div>
            <small className="text-muted">{emptyText}</small>
          </div>
        ) : (
          children
        )}

        {to && !loading && (
          <Link to={to} className="ra-all mt-auto pt-3">
            {toLabel} →
          </Link>
        )}
      </Card.Body>
    </Card>
  );
}

/* Recent support tickets, orders, user registrations and activity feed. */
function RecentActivity() {
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [activities, setActivities] = useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async (url, setter) => {
      try {
        const { data } = await axios.get(url);
        setter(data);
      } catch (error) {
        console.log(error);
      }
    };

    Promise.all([
      load(`${API}/tickets/recent`, setRecentTickets),
      load(`${API}/admin/recent-orders`, setRecentOrders),
      load(`${API}/admin/recent-users`, setRecentUsers),
      load(`${API}/admin/activities`, setActivities)
    ]).finally(() => setLoading(false));
  }, []);

  const openTickets = recentTickets.filter((t) => t.status === 'Open').length;

  return (
    <>
      <style>{recentActivityStyles}</style>

      <Row className="g-4 mb-4">
        {/* ===== Recent orders ===== */}
        <Col lg={7}>
          <Panel
            icon="🧾"
            title="Recent Orders"
            hint="Latest 5 orders placed"
            to="/admin/orders"
            toLabel="View all orders"
            loading={loading}
            isEmpty={recentOrders.length === 0}
            emptyIcon="🛒"
            emptyText="No orders yet. They'll show up here the moment one lands."
          >
            <div className="d-flex flex-column gap-2">
              {recentOrders.map((order) => {
                const tone = toneFor(ORDER_TONES, order.status);

                return (
                  <div
                    className="ra-order"
                    key={order._id}
                    style={{
                      '--tone': tone.color,
                      '--tone-soft': tone.soft,
                      '--tone-border': tone.border
                    }}
                  >
                    <span className="ra-avatar">
                      {initial(order.userId?.name)}
                    </span>

                    <div className="ra-order-info">
                      <div className="ra-order-top">
                        <span className="ra-order-name">
                          {order.userId?.name || 'Deleted user'}
                        </span>

                        <span className="ra-order-id">
                          #{order._id.slice(-6)}
                        </span>
                      </div>

                      <div className="ra-order-sub" title={order.userId?.email}>
                        {order.userId?.email || 'No email on file'}
                      </div>
                    </div>

                    <div className="ra-order-right">
                      <div className="ra-amount">
                        ₹{Number(order.total || 0).toLocaleString('en-IN')}
                      </div>

                      <div className="d-flex align-items-center gap-2 justify-content-end">
                        <span className="ra-pill">
                          <span className="ra-pill-dot" />
                          {order.status}
                        </span>
                      </div>

                      <div
                        className="ra-time"
                        title={fullDate(order.createdAt)}
                      >
                        {timeAgo(order.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
        </Col>

        {/* ===== Activity timeline ===== */}
        <Col lg={5}>
          <Panel
            icon="⚡"
            title="Activity Feed"
            hint="Everything happening across the store"
            loading={loading}
            isEmpty={activities.length === 0}
            emptyIcon="💤"
            emptyText="No activity recorded yet."
            skeleton={5}
          >
            <div className="ra-timeline">
              {activities.map((activity, index) => {
                const tone = activityTone(activity.message);

                return (
                  <div
                    className="ra-event"
                    key={index}
                    style={{ '--tone': tone.color, '--tone-soft': tone.soft }}
                  >
                    <span className="ra-event-dot">{activity.icon}</span>

                    <div className="ra-event-body">
                      <div className="ra-event-msg">{activity.message}</div>

                      <div className="ra-time" title={fullDate(activity.date)}>
                        {timeAgo(activity.date)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
        </Col>
      </Row>

      <Row className="g-4 mb-5">
        {/* ===== Recent tickets ===== */}
        <Col lg={6}>
          <Panel
            icon="🎫"
            title="Recent Support Tickets"
            hint={
              openTickets > 0
                ? `${openTickets} of these still open`
                : 'Latest 5 tickets raised'
            }
            to="/admin/tickets"
            toLabel="View all tickets"
            loading={loading}
            isEmpty={recentTickets.length === 0}
            emptyIcon="📭"
            emptyText="No support tickets. Nothing to answer right now."
          >
            <div className="d-flex flex-column gap-2">
              {recentTickets.map((ticket) => {
                const tone = toneFor(TICKET_TONES, ticket.status);

                return (
                  <Link
                    to={`/admin/tickets/${ticket._id}`}
                    className="ra-ticket"
                    key={ticket._id}
                    style={{
                      '--tone': tone.color,
                      '--tone-soft': tone.soft,
                      '--tone-border': tone.border
                    }}
                  >
                    <span className="ra-ticket-accent" />

                    <div className="ra-ticket-info">
                      <div className="ra-ticket-subject" title={ticket.subject}>
                        {ticket.subject}
                      </div>

                      <div className="ra-ticket-meta">
                        <span>📦 {ticket.productName || 'General enquiry'}</span>

                        <span
                          className="ra-time"
                          title={fullDate(ticket.createdAt)}
                        >
                          {timeAgo(ticket.createdAt)}
                        </span>
                      </div>
                    </div>

                    <span className="ra-pill">
                      <span className="ra-pill-dot" />
                      {ticket.status}
                    </span>
                  </Link>
                );
              })}
            </div>
          </Panel>
        </Col>

        {/* ===== Recent users ===== */}
        <Col lg={6}>
          <Panel
            icon="👤"
            title="New Registrations"
            hint="Latest 5 customers to sign up"
            to="/admin/users"
            toLabel="View all users"
            loading={loading}
            isEmpty={recentUsers.length === 0}
            emptyIcon="🙋"
            emptyText="No registrations yet."
          >
            <div className="d-flex flex-column gap-2">
              {recentUsers.map((user) => {
                /* Flag anyone who joined in the last 24 hours */
                const isNew =
                  Date.now() - new Date(user.createdAt).getTime() <
                  24 * 60 * 60 * 1000;

                return (
                  <div className="ra-user" key={user._id}>
                    <span className="ra-avatar ra-avatar-violet">
                      {initial(user.name)}
                    </span>

                    <div className="ra-user-info">
                      <div className="ra-user-name">
                        {user.name}
                        {isNew && <span className="ra-new">NEW</span>}
                      </div>

                      <div className="ra-user-email" title={user.email}>
                        {user.email}
                      </div>
                    </div>

                    <div className="ra-time" title={fullDate(user.createdAt)}>
                      {timeAgo(user.createdAt)}
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
        </Col>
      </Row>
    </>
  );
}

const recentActivityStyles = `
  .ra-card {
    border-radius: 20px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(124, 45, 18, 0.08);
    transition: box-shadow 0.25s ease;
  }
  .ra-card:hover {
    box-shadow: 0 16px 34px rgba(124, 45, 18, 0.13);
  }

  /* Header */
  .ra-icon {
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    border-radius: 14px;
    background: linear-gradient(135deg, #fff7ed, #ffedd5);
    border: 1px solid #fed7aa;
  }
  .ra-title {
    font-weight: 800;
    margin: 0;
    font-size: 1.05rem;
    color: #7c2d12;
  }
  .ra-hint {
    color: #a8a29e;
    font-size: 0.76rem;
  }
  .ra-rule {
    height: 1px;
    background: linear-gradient(90deg, #fed7aa, transparent);
    margin: 14px 0;
  }
  .min-w-0 { min-width: 0; }

  /* Shared bits */
  .ra-avatar {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    font-weight: 800;
    font-size: 1rem;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
  }
  .ra-avatar-violet {
    background: linear-gradient(135deg, #7c3aed, #a855f7);
  }
  .ra-time {
    font-size: 0.7rem;
    color: #a8a29e;
    font-weight: 600;
    white-space: nowrap;
  }
  .ra-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.68rem;
    font-weight: 800;
    color: var(--tone);
    background: var(--tone-soft);
    border: 1px solid var(--tone-border);
    border-radius: 20px;
    padding: 3px 10px;
    white-space: nowrap;
  }
  .ra-pill-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--tone);
    flex-shrink: 0;
  }
  .ra-all {
    font-size: 0.8rem;
    font-weight: 700;
    color: #ea580c;
    text-decoration: none;
    align-self: flex-start;
  }
  .ra-all:hover { color: #c2410c; text-decoration: underline; }

  /* Orders */
  .ra-order {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 14px;
    border: 1px solid #f3efe8;
    background: #fdfcfa;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }
  .ra-order:hover {
    transform: translateX(3px);
    border-color: var(--tone-border);
    box-shadow: 0 8px 20px rgba(124, 45, 18, 0.10);
  }
  .ra-order-info { flex: 1 1 auto; min-width: 0; }
  .ra-order-top {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }
  .ra-order-name {
    font-weight: 700;
    font-size: 0.92rem;
    color: #1c1917;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ra-order-id {
    font-size: 0.7rem;
    font-weight: 700;
    color: #a8a29e;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }
  .ra-order-sub {
    font-size: 0.76rem;
    color: #78716c;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ra-order-right {
    text-align: right;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }
  .ra-amount {
    font-weight: 800;
    font-size: 1rem;
    color: #15803d;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }

  /* Activity timeline */
  .ra-timeline {
    position: relative;
    padding-left: 6px;
  }
  .ra-event {
    position: relative;
    display: flex;
    gap: 12px;
    padding-bottom: 16px;
  }
  /* Connector line between events */
  .ra-event:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 17px;
    top: 34px;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, #f1e4d0, #faf7f2);
  }
  .ra-event-dot {
    position: relative;
    z-index: 1;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    border-radius: 50%;
    background: var(--tone-soft);
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px var(--tone);
  }
  .ra-event-body { min-width: 0; padding-top: 2px; }
  .ra-event-msg {
    font-size: 0.86rem;
    font-weight: 600;
    color: #292524;
    line-height: 1.35;
  }

  /* Tickets */
  .ra-ticket {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px 10px 16px;
    border-radius: 14px;
    border: 1px solid #f3efe8;
    background: #fdfcfa;
    overflow: hidden;
    text-decoration: none;
    color: inherit;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }
  .ra-ticket:hover {
    transform: translateX(3px);
    color: inherit;
    border-color: var(--tone-border);
    box-shadow: 0 8px 20px rgba(124, 45, 18, 0.10);
  }
  .ra-ticket-accent {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: var(--tone);
  }
  .ra-ticket-info { flex: 1 1 auto; min-width: 0; }
  .ra-ticket-subject {
    font-weight: 700;
    font-size: 0.9rem;
    color: #1c1917;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ra-ticket-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 3px;
    font-size: 0.74rem;
    color: #78716c;
    min-width: 0;
  }
  .ra-ticket-meta > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Users */
  .ra-user {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 14px;
    border: 1px solid #f3efe8;
    background: #fdfcfa;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .ra-user:hover {
    transform: translateX(3px);
    box-shadow: 0 8px 20px rgba(124, 45, 18, 0.10);
  }
  .ra-user-info { flex: 1 1 auto; min-width: 0; }
  .ra-user-name {
    display: flex;
    align-items: center;
    gap: 7px;
    font-weight: 700;
    font-size: 0.9rem;
    color: #1c1917;
  }
  .ra-user-email {
    font-size: 0.76rem;
    color: #78716c;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ra-new {
    font-size: 0.58rem;
    font-weight: 800;
    letter-spacing: 0.5px;
    color: #fff;
    background: linear-gradient(120deg, #16a34a, #22c55e);
    border-radius: 20px;
    padding: 2px 7px;
    flex-shrink: 0;
  }

  /* Empty + loading */
  .ra-empty {
    text-align: center;
    padding: 32px 12px;
  }
  .ra-empty-icon {
    font-size: 2.4rem;
    margin-bottom: 6px;
  }
  .ra-skel-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 14px;
    border: 1px solid #f3efe8;
    background: #fdfcfa;
  }
  .ra-skel {
    display: block;
    background: linear-gradient(90deg, #f1ece4 25%, #faf7f2 37%, #f1ece4 63%);
    background-size: 400% 100%;
    animation: raShimmer 1.4s ease infinite;
    border-radius: 8px;
  }
  .ra-skel-dot { width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0; }
  .ra-skel-line { height: 11px; width: 65%; margin-bottom: 8px; }
  .ra-skel-line.short { width: 40%; margin-bottom: 0; }
  @keyframes raShimmer {
    0%   { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  @media (max-width: 575.98px) {
    .ra-icon { width: 36px; height: 36px; font-size: 1rem; }
    .ra-title { font-size: 0.95rem; }
    .ra-order { flex-wrap: wrap; }
    .ra-order-right { width: 100%; flex-direction: row; align-items: center; justify-content: space-between; }
    .ra-avatar { width: 34px; height: 34px; font-size: 0.85rem; }
  }

  @media (prefers-reduced-motion: reduce) {
    .ra-card, .ra-order, .ra-ticket, .ra-user, .ra-skel {
      transition: none;
      animation: none;
    }
  }
`;

export default RecentActivity;
